import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  PaperPlaneTiltIcon,
  GlobeIcon,
  TerminalIcon,
  CodeIcon,
  FilesIcon,
  CloudIcon,
  PlugsIcon,
  LightningIcon,
} from "@phosphor-icons/react";

function ConnectorCard({ icon: Icon, name, status, desc, configHref }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-sm border border-zinc-800 bg-black flex items-center justify-center">
          <Icon size={20} className="text-white" weight="duotone" />
        </div>
        <Badge
          variant="outline"
          className={`rounded-sm font-mono text-[10px] ${
            status === "active"
              ? "border-emerald-700 text-emerald-300 bg-emerald-950/30"
              : status === "ready"
                ? "border-zinc-700 text-zinc-300 bg-zinc-950"
                : "border-zinc-800 text-zinc-600 bg-zinc-950"
          }`}
        >
          {status}
        </Badge>
      </div>
      <div>
        <div className="font-heading text-lg tracking-tight">{name}</div>
        <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{desc}</div>
      </div>
      {configHref && (
        <a
          href={configHref}
          className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white"
        >
          configure →
        </a>
      )}
    </div>
  );
}

export default function Connections() {
  const [providers, setProviders] = useState([]);
  const [tg, setTg] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/providers"),
      api.get("/telegram/status"),
      api.get("/settings"),
    ]).then(([p, t, s]) => {
      setProviders(p.data);
      setTg(t.data);
      setSettings(s.data);
    });
  }, []);

  return (
    <div className="h-full overflow-y-auto" data-testid="connections-page">
      <div className="max-w-6xl mx-auto p-8 fade-up">
        <div className="mb-2 font-overline text-[10px] text-zinc-500">
          External Interfaces
        </div>
        <h1 className="font-heading text-4xl tracking-tighter font-medium mb-2">
          Connections
        </h1>
        <p className="text-sm text-zinc-500 max-w-2xl mb-10">
          Channels where your DoppelGanger can listen and act. Each connector
          shares the same agent loop, tools, and knowledge base.
        </p>

        <div className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            Messaging Channels
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ConnectorCard
              icon={PaperPlaneTiltIcon}
              name="Telegram"
              status={
                tg?.running
                  ? "active"
                  : tg?.token_configured
                    ? "ready"
                    : "needs token"
              }
              desc="Talk to your agent from any device via your Telegram bot. Polling mode; no public URL needed."
              configHref="/settings"
            />
            <ConnectorCard
              icon={GlobeIcon}
              name="Web UI"
              status="active"
              desc="This dashboard. Built-in Telegram-style chat with tool execution view."
            />
            <ConnectorCard
              icon={PlugsIcon}
              name="REST API"
              status="active"
              desc="POST /api/chat/send with a bearer token. Wire DoppelGanger into Slack, Discord, voice, anything."
            />
            <ConnectorCard
              icon={PlugsIcon}
              name="Inbound Webhook"
              status={settings?.webhook_inbound_secret ? "active" : "ready"}
              desc="POST /api/webhook/{secret} — public token-gated endpoint. Zapier, n8n, IFTTT, custom apps. Configure secret in Settings."
              configHref="/settings"
            />
            <ConnectorCard
              icon={PlugsIcon}
              name="Outbound Webhook"
              status={settings?.webhook_outbound_enabled ? "active" : "off"}
              desc="DoppelGanger POSTs each agent reply to your URL. Mirror to Slack incoming webhook, Discord webhook, etc."
              configHref="/settings"
            />
          </div>
        </div>

        <div className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            LLM Providers
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {providers.map((p) => (
              <ConnectorCard
                key={p.id}
                icon={
                  p.type === "openrouter"
                    ? CloudIcon
                    : p.type === "ollama_cloud"
                      ? CloudIcon
                      : LightningIcon
                }
                name={p.name}
                status={
                  settings?.active_provider_id === p.id
                    ? "active"
                    : p.enabled
                      ? "ready"
                      : "disabled"
                }
                desc={p.base_url}
                configHref="/settings"
              />
            ))}
          </div>
        </div>

        <div className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            Agent Tools
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ConnectorCard
              icon={GlobeIcon}
              name="Web Search"
              status={settings?.tools_enabled?.web_search ? "active" : "off"}
              desc="DuckDuckGo search returning titles, URLs, snippets."
            />
            <ConnectorCard
              icon={FilesIcon}
              name="File System"
              status={
                settings?.tools_enabled?.read_file ||
                settings?.tools_enabled?.write_file
                  ? "active"
                  : "off"
              }
              desc={
                settings?.sandboxed_execution
                  ? "Sandboxed to /app/agent_workspace."
                  : "Full host access (unsandboxed)."
              }
            />
            <ConnectorCard
              icon={CodeIcon}
              name="Python Runtime"
              status={
                settings?.tools_enabled?.execute_python ? "active" : "off"
              }
              desc="Subprocess execution with timeout."
            />
            <ConnectorCard
              icon={TerminalIcon}
              name="Shell"
              status={
                settings?.tools_enabled?.execute_shell ? "active" : "off"
              }
              desc="bash -c command execution with timeout."
            />
          </div>
        </div>

        <div className="border border-dashed border-zinc-800 bg-zinc-950 rounded-sm p-6 text-center">
          <div className="font-overline text-[10px] text-zinc-500 mb-2">
            More Connectors
          </div>
          <div className="text-sm text-zinc-400">
            Slack · Discord can be wired via the Inbound/Outbound Webhooks
            today. Native bots, voice, and email coming next.
          </div>
        </div>
      </div>
    </div>
  );
}
